import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { createChartFlow, getMeasurementsExtremesAll } from '../services/device';
import { extractDeviceType } from '../utils/device';
import './MonthlyReport.css';

const DEVICE_TYPES = [
    { key: 'ALL', label: '所有類型' },
    { key: 'RA', label: '雨量計' },
    { key: 'ALC', label: '荷重計' },
    { key: 'SIS', label: '坍方計' },
    { key: 'OW', label: '水位觀測井' },
    { key: 'TI', label: '傾斜計' },
];

const DEFAULT_START_YEAR = 2022;
const LATEST_REPORT_MONTH = { year: 2025, month: 12 };

const getBaseApiUrl = () => import.meta.env.VITE_API_BASE_URL || '/api';

const toMonthKey = (year, month) =>
    `${year}-${String(month).padStart(2, '0')}`;

const toDateString = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
        date.getDate()
    ).padStart(2, '0')}`;

const normalizeValue = (value) => {
    if (value == null) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const trimmed = `${value}`.trim();
    if (!trimmed) return null;
    const num = Number(trimmed);
    if (Number.isFinite(num)) return num;
    return trimmed;
};

const extractMeasurementValue = (extremes) => {
    if (!extremes || typeof extremes !== 'object') return null;

    const hasTiKeys =
        'max_13' in extremes ||
        'max_24' in extremes ||
        'min_13' in extremes ||
        'min_24' in extremes;
    if (hasTiKeys) {
        return {
            max13: normalizeValue(extremes.max_13),
            min13: normalizeValue(extremes.min_13),
            max24: normalizeValue(extremes.max_24),
            min24: normalizeValue(extremes.min_24),
        };
    }

    const maxValue = normalizeValue(extremes.max_value ?? extremes.max);
    const minValue = normalizeValue(extremes.min_value ?? extremes.min);
    return maxValue ?? minValue ?? null;
};

const hasMeasurementValue = (extremes) => {
    if (!extremes || typeof extremes !== 'object') return false;
    return (
        normalizeValue(extremes.max_value ?? extremes.max) != null ||
        normalizeValue(extremes.min_value ?? extremes.min) != null ||
        normalizeValue(extremes.max_13) != null ||
        normalizeValue(extremes.max_24) != null ||
        normalizeValue(extremes.min_13) != null ||
        normalizeValue(extremes.min_24) != null
    );
};

const getDeviceDistrict = (device) =>
    device?.district ||
    device?.slope_district ||
    device?.slopeDistrict ||
    device?.segment ||
    device?.area ||
    device?.region ||
    device?.slope?.district ||
    device?.slope?.segment ||
    '未分區';

const getDeviceInid = (device) =>
    device?.device_inid || '-';

const getDeviceName = (device) =>
    device?.name || device?.device_name || device?.deviceName || device?.device || '';

const getDeviceId = (device) =>
    device?.id || device?.device_id || device?.deviceId || device?.deviceID || null;

const getChartKey = (device) => {
    const key = device?.id ?? device?.name;
    if (key == null) return null;
    return String(key);
};

const DEVICE_TYPE_ALIASES = {
    ra: 'RA',
    rain: 'RA',
    rainfall: 'RA',
    alc: 'ALC',
    force: 'ALC',
    load: 'ALC',
    sis: 'SIS',
    landslide: 'SIS',
    ls: 'SIS',
    ow: 'OW',
    water_level: 'OW',
    waterlevel: 'OW',
    ti: 'TI',
    tilt: 'TI',
};

const normalizeDeviceType = (rawType, deviceName) => {
    if (rawType == null || `${rawType}`.trim() === '') {
        return extractDeviceType(deviceName);
    }
    const trimmed = `${rawType}`.trim();
    const upper = trimmed.toUpperCase();
    if (DEVICE_TYPES.some((type) => type.key === upper)) return upper;
    const aliasKey = DEVICE_TYPE_ALIASES[trimmed.toLowerCase()];
    return aliasKey || extractDeviceType(deviceName);
};

const getDeviceTypeLabel = (typeKey, rawType) => {
    const match = DEVICE_TYPES.find((type) => type.key === typeKey);
    if (match) return match.label;
    if (rawType == null) return '未分類';
    const text = `${rawType}`.trim();
    return text || '未分類';
};

const getChartUrl = (monthKey, deviceName, cacheKey) => {
    if (!monthKey || !deviceName) return null;
    const base = getBaseApiUrl();
    const url = `${base}/charts/${monthKey}/${encodeURIComponent(deviceName)}.png`;
    if (!cacheKey) return url;
    return `${url}?t=${encodeURIComponent(cacheKey)}`;
};

const buildReportFilename = (typeKey, year, month) =>
    `${typeKey}_${year}_${String(month).padStart(2, '0')}.docx`;

const getReportUrl = (typeKey, year, month) => {
    if (!typeKey) return null;
    const monthKey = toMonthKey(year, month);
    const filename = buildReportFilename(typeKey, year, month);
    const base = getBaseApiUrl();
    return `${base}/reports/${monthKey}/${filename}`;
};

const buildDevicesFromMeasurements = (dataList) => {
    if (!Array.isArray(dataList)) return [];
    const devices = new Map();
    dataList.forEach((item) => {
        const name = getDeviceName(item);
        const id = getDeviceId(item);
        if (!name && id == null) return;
        const rawType =
            item?.device_type ||
            item?.deviceType ||
            item?.type ||
            item?.category ||
            item?.kind;
        const typeKey = normalizeDeviceType(rawType, name) || 'UNKNOWN';
        const typeLabel = getDeviceTypeLabel(typeKey, rawType);
        const key = id ?? name;
        if (devices.has(key)) return;
        devices.set(key, {
            raw: item,
            id,
            name,
            typeKey,
            typeLabel,
            district: getDeviceDistrict(item),
            deviceInid: getDeviceInid(item),
        });
    });
    return Array.from(devices.values());
};

const renderMeasurementValue = (device, extremes) => {
    const value = extractMeasurementValue(extremes);
    if (device.typeKey === 'TI' && value && typeof value === 'object') {
        const xLabel = `最大 ${value.max13 ?? '-'}`;
        const xValue = `最小 ${value.min13 ?? '-'}`;
        const yLabel = `最大 ${value.max24 ?? '-'}`;
        const yValue = `最小 ${value.min24 ?? '-'}`;
        return (
            <div className="monthly-report__ti-measurement">
                <div className="monthly-report__ti-row">
                    <span className="monthly-report__ti-axis">軸13</span>
                    <span className="monthly-report__ti-values">
                        {xLabel} / {xValue}
                    </span>
                </div>
                <div className="monthly-report__ti-row">
                    <span className="monthly-report__ti-axis">軸24</span>
                    <span className="monthly-report__ti-values">
                        {yLabel} / {yValue}
                    </span>
                </div>
            </div>
        );
    }
    const maxValue = normalizeValue(extremes?.max_value ?? extremes?.max);
    const minValue = normalizeValue(extremes?.min_value ?? extremes?.min);
    const maxLabel = `最大值：${maxValue ?? '-'}`;
    const minLabel = `最小值：${minValue ?? '-'}`;
    return (
        <div className="monthly-report__ti-measurement">
            <div>{maxLabel}</div>
            <div>{minLabel}</div>
        </div>
    );
};

export default function MonthlyReport() {
    const { user } = useAuth();
    const { slopeData } = useOutletContext() || {};
    const roleValue = user?.role || user?.role_name || user?.roleName || '';
    const roleText = roleValue ? roleValue.toString() : '';
    const roleLower = roleText.toLowerCase();
    const canViewMonthlyReport =
        roleLower.includes('admin') || roleText.includes('陸島') || roleLower.includes('land');

    if (!canViewMonthlyReport) {
        return <Navigate to="/" replace />;
    }
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const [selectedType, setSelectedType] = useState('ALL');
    const [selectedYear, setSelectedYear] = useState(lastMonth.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(lastMonth.getMonth() + 1);
    const [isLoading, setIsLoading] = useState(false);
    const [measurementsList, setMeasurementsList] = useState([]);
    const [measurementById, setMeasurementById] = useState({});
    const [chartStatus, setChartStatus] = useState({});
    const [flowStatusById, setFlowStatusById] = useState({});
    const [chartRefreshById, setChartRefreshById] = useState({});
    const [isDownloadingReport, setIsDownloadingReport] = useState(false);
    const cacheRef = useRef(new Map());
    const chartTimeoutRef = useRef(new Map());
    const chartImageRef = useRef(new Map());
    const galleryRef = useRef(null);
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [galleryIndex, setGalleryIndex] = useState(0);
    const thumbsRef = useRef(null);

    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = DEFAULT_START_YEAR; year <= currentYear; year += 1) {
            years.push(year);
        }
        return years;
    }, []);

    const allDevices = useMemo(
        () => buildDevicesFromMeasurements(measurementsList),
        [measurementsList]
    );

    const filteredDevices = useMemo(() => {
        if (selectedType === 'ALL') return allDevices;
        return allDevices.filter((device) => device.typeKey === selectedType);
    }, [allDevices, selectedType]);

    const monthKey = useMemo(
        () => toMonthKey(selectedYear, selectedMonth),
        [selectedYear, selectedMonth]
    );

    const latestMonthIndex =
        LATEST_REPORT_MONTH.year * 12 + (LATEST_REPORT_MONTH.month - 1);
    const selectedMonthIndex = selectedYear * 12 + (selectedMonth - 1);
    const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthIndex =
        lastMonthDate.getFullYear() * 12 + lastMonthDate.getMonth();
    const isWithinAvailableRange =
        lastMonthIndex >= latestMonthIndex &&
        selectedMonthIndex >= latestMonthIndex &&
        selectedMonthIndex <= lastMonthIndex;

    const { startTime, endTime } = useMemo(() => {
        const start = new Date(selectedYear, selectedMonth - 1, 1);
        const end = new Date(selectedYear, selectedMonth, 1);
        return {
            startTime: `${toDateString(start)}T00:00`,
            endTime: `${toDateString(end)}T00:00`,
        };
    }, [selectedYear, selectedMonth]);

    const canCreateChartFlow = useMemo(() => {
        const endDate = new Date(selectedYear, selectedMonth, 1);
        return Date.now() > endDate.getTime();
    }, [selectedYear, selectedMonth]);

    const groupedDevices = useMemo(() => {
        const groups = new Map();
        const order = [];

        filteredDevices.forEach((device) => {
            const district = device.district || '未分區';
            if (!groups.has(district)) {
                groups.set(district, []);
                order.push(district);
            }
            groups.get(district).push(device);
        });

        return order.map((district) => ({
            district,
            devices: groups.get(district) || [],
        }));
    }, [filteredDevices]);

    const canDownloadReport =
        selectedType !== 'ALL' &&
        filteredDevices.length > 0 &&
        isWithinAvailableRange;

    const galleryItems = useMemo(() => {
        return filteredDevices
            .map((device) => {
                const chartKey = getChartKey(device);
                const cacheKey = chartKey ? chartRefreshById[chartKey] : undefined;
                const url = getChartUrl(monthKey, device.name, cacheKey);
                const status = chartKey ? chartStatus[chartKey] : null;
                const failed = status === 'error';
                return {
                    id: chartKey,
                    name: device.name,
                    typeLabel: device.typeLabel,
                    url,
                    failed,
                };
            })
            .filter((item) => !!item.url);
    }, [filteredDevices, monthKey, chartStatus, chartRefreshById]);

    const expectedChartKeys = useMemo(() => {
        const keys = new Set();
        filteredDevices.forEach((device) => {
            if (!getChartUrl(monthKey, device.name)) return;
            const chartKey = getChartKey(device);
            if (chartKey) keys.add(chartKey);
        });
        return Array.from(keys);
    }, [filteredDevices, monthKey]);

    const isPageLoading = isLoading;

    useEffect(() => {
        setChartStatus({});
        chartTimeoutRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
        chartTimeoutRef.current.clear();
    }, [monthKey]);

    useEffect(() => {
        setFlowStatusById({});
        setChartRefreshById({});
    }, [monthKey]);

    useEffect(() => {
        if (!isGalleryOpen) return;
        const handleKey = (event) => {
            if (event.key === 'Escape') {
                setIsGalleryOpen(false);
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [isGalleryOpen]);

    useEffect(() => {
        if (!isGalleryOpen || !thumbsRef.current) return;
        const activeThumb = thumbsRef.current.querySelector(
            '.monthly-report__thumb--active'
        );
        if (activeThumb && typeof activeThumb.scrollIntoView === 'function') {
            activeThumb.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center',
            });
        }
    }, [galleryIndex, isGalleryOpen]);

    useEffect(() => {
        const expectedSet = new Set(expectedChartKeys);

        expectedChartKeys.forEach((key) => {
            if (chartStatus[key]) return;
            if (chartTimeoutRef.current.has(key)) return;
            const timeoutId = setTimeout(() => {
                setChartStatus((prev) =>
                    prev[key] ? prev : { ...prev, [key]: 'timeout' }
                );
                chartTimeoutRef.current.delete(key);
            }, 12000);
            chartTimeoutRef.current.set(key, timeoutId);
        });

        chartTimeoutRef.current.forEach((timeoutId, key) => {
            if (!expectedSet.has(key)) {
                clearTimeout(timeoutId);
                chartTimeoutRef.current.delete(key);
            }
        });
    }, [expectedChartKeys, chartStatus]);

    useEffect(() => {
        const updates = {};
        expectedChartKeys.forEach((key) => {
            if (chartStatus[key]) return;
            const img = chartImageRef.current.get(key);
            if (img && img.complete && img.naturalWidth > 0) {
                updates[key] = 'loaded';
            }
        });
        if (Object.keys(updates).length > 0) {
            setChartStatus((prev) => ({ ...prev, ...updates }));
        }
    }, [expectedChartKeys, chartStatus]);

    useEffect(() => {
        let canceled = false;
        const controller = new AbortController();

        const loadMeasurements = async () => {
            setIsLoading(true);
            const cacheKey = `all-${monthKey}`;
            let dataList = cacheRef.current.get(cacheKey);
            if (!dataList) {
                dataList = await getMeasurementsExtremesAll({
                    startDate: startTime,
                    endDate: endTime,
                    signal: controller.signal,
                });
                cacheRef.current.set(cacheKey, dataList);
            }

            if (canceled) return;
            const next = {};
            const normalizedList = Array.isArray(dataList) ? dataList : [];
            normalizedList.forEach((item) => {
                const deviceId = item?.device_id ?? item?.deviceId;
                if (deviceId == null) return;
                next[deviceId] = item;
            });
            setMeasurementsList(normalizedList);
            setMeasurementById(next);
            setIsLoading(false);
        };

        loadMeasurements().catch((error) => {
            if (!canceled) {
                // eslint-disable-next-line no-console
                console.error('Failed to fetch monthly measurements:', error);
                setMeasurementsList([]);
                setIsLoading(false);
            }
        });

        return () => {
            canceled = true;
            controller.abort();
        };
    }, [monthKey, startTime, endTime]);

    const handleChartLoad = (chartKey) => {
        if (!chartKey) return;
        const timeoutId = chartTimeoutRef.current.get(chartKey);
        if (timeoutId) {
            clearTimeout(timeoutId);
            chartTimeoutRef.current.delete(chartKey);
        }
        setChartStatus((prev) => ({ ...prev, [chartKey]: 'loaded' }));
    };

    const handleChartError = (chartKey) => {
        if (!chartKey) return;
        const timeoutId = chartTimeoutRef.current.get(chartKey);
        if (timeoutId) {
            clearTimeout(timeoutId);
            chartTimeoutRef.current.delete(chartKey);
        }
        setChartStatus((prev) => ({ ...prev, [chartKey]: 'error' }));
    };

    const refreshDeviceChart = (device) => {
        const chartKey = getChartKey(device);
        if (!chartKey) return;
        setChartRefreshById((prev) => ({ ...prev, [chartKey]: Date.now() }));
        const timeoutId = chartTimeoutRef.current.get(chartKey);
        if (timeoutId) {
            clearTimeout(timeoutId);
            chartTimeoutRef.current.delete(chartKey);
        }
        setChartStatus((prev) => {
            if (!prev[chartKey]) return prev;
            const next = { ...prev };
            delete next[chartKey];
            return next;
        });
    };

    const getFlowFailureMessage = (message) => {
        if (message === 'create_error') return '建立失敗';
        if (
            [
                'run_failed',
                'run_error',
                'run_unknown_state',
                'run_timeout',
                'run_max_polls',
            ].includes(message)
        ) {
            return '執行失敗';
        }
        return '執行失敗';
    };

    const handleCreateChartFlow = async (device) => {
        if (!device?.name || !canCreateChartFlow) return;
        const key = device.id ?? device.name;
        if (flowStatusById[key] === 'loading') return;
        setFlowStatusById((prev) => ({ ...prev, [key]: 'loading' }));
        try {
            const result = await createChartFlow({
                deviceName: device.name,
                end: endTime,
            });
            const status = `${result?.status || ''}`.toUpperCase();
            const message = result?.message || '';
            if (status === 'SUCCESS' && (message === 'runing' || message === 'running')) {
                setFlowStatusById((prev) => ({ ...prev, [key]: 'loading' }));
                return;
            }
            if (status === 'SUCCESS' && message === 'run_completed') {
                setFlowStatusById((prev) => ({ ...prev, [key]: 'success' }));
                refreshDeviceChart(device);
                return;
            }
            if (status === 'ERROR') {
                window.alert(getFlowFailureMessage(message));
                setFlowStatusById((prev) => ({ ...prev, [key]: 'error' }));
                return;
            }
            window.alert(getFlowFailureMessage(message));
            setFlowStatusById((prev) => ({ ...prev, [key]: 'error' }));
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to create chart flow:', error);
            window.alert('執行失敗');
            setFlowStatusById((prev) => ({ ...prev, [key]: 'error' }));
        }
    };

    const handleDownloadReport = async () => {
        if (!canDownloadReport || isDownloadingReport) return;
        setIsDownloadingReport(true);
        const url = getReportUrl(selectedType, selectedYear, selectedMonth);
        const filename = buildReportFilename(
            selectedType,
            selectedYear,
            selectedMonth
        );
        const showDownloadError = () =>
            window.alert('找不到該檔案 請稍後重試');
        try {
            const response = await fetch(url, { credentials: 'include' });
            const contentType = response.headers.get('content-type') || '';
            const isDocx = contentType.includes(
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            );
            if (!response.ok || !isDocx) {
                showDownloadError();
                return;
            }
            const blob = await response.blob();
            const objectUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(objectUrl);
        } catch (error) {
            showDownloadError();
        } finally {
            setIsDownloadingReport(false);
        }
    };

    const openGallery = (index) => {
        if (!galleryItems.length) return;
        setGalleryIndex(index);
        setIsGalleryOpen(true);
    };

    const goPrev = () => {
        setGalleryIndex((prev) =>
            prev === 0 ? galleryItems.length - 1 : prev - 1
        );
    };

    const goNext = () => {
        setGalleryIndex((prev) =>
            prev === galleryItems.length - 1 ? 0 : prev + 1
        );
    };

    const toggleFullscreen = async () => {
        if (!galleryRef.current) return;
        if (document.fullscreenElement) {
            await document.exitFullscreen();
        } else {
            await galleryRef.current.requestFullscreen();
        }
    };

    return (
        <div className="monthly-report">
            <section className="monthly-report__filters">
                <div className="monthly-report__filter">
                    <label htmlFor="monthly-report-type"></label>
                    <select
                        id="monthly-report-type"
                        value={selectedType}
                        onChange={(event) => setSelectedType(event.target.value)}
                    >
                        {DEVICE_TYPES.map((type) => (
                            <option key={type.key} value={type.key}>
                                {type.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="monthly-report__filter">
                    <label htmlFor="monthly-report-year"></label>
                    <select
                        id="monthly-report-year"
                        value={selectedYear}
                        onChange={(event) => setSelectedYear(Number(event.target.value))}
                    >
                        {yearOptions.map((year) => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="monthly-report__filter">
                    <label htmlFor="monthly-report-month"></label>
                    <select
                        id="monthly-report-month"
                        value={selectedMonth}
                        onChange={(event) => setSelectedMonth(Number(event.target.value))}
                    >
                        {Array.from({ length: 12 }, (_, idx) => idx + 1).map(
                            (month) => (
                                <option key={month} value={month}>
                                    {month}
                                </option>
                            )
                        )}
                    </select>
                </div>
                {canDownloadReport && (
                    <button
                        type="button"
                        className={`monthly-report__download-btn${
                            isDownloadingReport
                                ? ' monthly-report__download-btn--loading'
                                : ''
                        }`}
                        onClick={handleDownloadReport}
                        disabled={isDownloadingReport}
                    >
                        {isDownloadingReport ? '下載中...' : '下載月報 DOCX'}
                    </button>
                )}
            </section>

            {isPageLoading && (
                <div className="monthly-report__loading-overlay">
                    <div className="monthly-report__loading">
                        <div className="monthly-report__loader" aria-hidden="true">
                            <div className="monthly-report__loader-core" />
                            <div className="monthly-report__loader-ring" />
                            <div className="monthly-report__loader-ring monthly-report__loader-ring--alt" />
                        </div>
                        <div className="monthly-report__loading-text">資料載入中...</div>
                        <div className="monthly-report__loading-dots" aria-hidden="true">
                            <span />
                            <span />
                            <span />
                        </div>
                    </div>
                </div>
            )}

            {groupedDevices.length === 0 ? (
                <div className="monthly-report__empty">查無符合條件的儀器</div>
            ) : (
                groupedDevices.map((group) => (
                    <section
                        key={group.district}
                        className="monthly-report__group"
                    >
                        <div className="monthly-report__group-title">
                            {group.district}
                        </div>
                        <div className="monthly-report__table">
                            <div className="monthly-report__row monthly-report__row--header">
                                <div>儀器名稱</div>
                                <div>類型</div>
                                <div>儀器編號</div>
                                <div>狀態</div>
                                <div>本月測量值</div>
                                <div>圖表1</div>
                                <div>圖表2</div>
                                <div>轉存圖表</div>
                            </div>
                            {group.devices.map((device) => {
                                const extremes =
                                    device.id != null
                                        ? measurementById[device.id]
                                        : null;
                                const measurementValue = renderMeasurementValue(
                                    device,
                                    extremes
                                );
                                const hasMeasurement = hasMeasurementValue(extremes);
                                const chartKey = getChartKey(device);
                                const cacheKey = chartKey
                                    ? chartRefreshById[chartKey] || undefined
                                    : undefined;
                                const chartUrl = getChartUrl(
                                    monthKey,
                                    device.name,
                                    cacheKey
                                );
                                const chartState = chartKey
                                    ? chartStatus[chartKey]
                                    : null;
                                const chartFailed = chartState === 'error';
                                const isChartReady =
                                    chartKey != null &&
                                    chartStatus[chartKey] === 'loaded';
                                const isChartLoading =
                                    !!chartUrl && !chartFailed && !isChartReady;
                                const isStored = hasMeasurement && isChartReady;
                                const statusText = isStored ? '已轉存' : '尚未轉存';
                                const statusClass = isStored
                                    ? 'monthly-report__status monthly-report__status--ok'
                                    : 'monthly-report__status monthly-report__status--pending';
                                const galleryIndex = galleryItems.findIndex(
                                    (item) => item.id === chartKey && item.url === chartUrl
                                );
                                const flowKey = device.id ?? device.name;
                                const flowStatus = flowStatusById[flowKey];
                                const flowLoading = flowStatus === 'loading';
                                const flowSuccess = flowStatus === 'success';
                                const flowError = flowStatus === 'error';
                                const flowLabel = flowSuccess
                                    ? '已完成'
                                    : flowLoading
                                      ? '處理中...'
                                      : flowError
                                        ? '重試'
                                        : '轉存圖表';
                                const flowDisabled =
                                    flowLoading || flowSuccess || !device.name;

                                return (
                                    <div
                                        key={`${device.typeKey}-${device.name}-${device.id}`}
                                        className="monthly-report__row"
                                    >
                                        <div className="monthly-report__device-name">
                                            {device.name || '-'}
                                        </div>
                                        <div>{device.typeLabel}</div>
                                        <div>{device.deviceInid || '-'}</div>
                                        <div className={statusClass}>{statusText}</div>
                                        <div className="monthly-report__measurement">
                                            {measurementValue}
                                        </div>
                                        <div className="monthly-report__chart">
                                            {chartUrl && !chartFailed ? (
                                                <div className="monthly-report__chart-frame">
                                                    {isChartLoading && (
                                                        <span
                                                            className="monthly-report__chart-spinner"
                                                            aria-label="圖表載入中"
                                                        />
                                                    )}
                                                    <img
                                                        src={chartUrl}
                                                        alt={`${device.name || '設備'}圖表`}
                                                        ref={(node) => {
                                                            if (!chartKey) return;
                                                            if (node) {
                                                                chartImageRef.current.set(
                                                                    chartKey,
                                                                    node
                                                                );
                                                            } else {
                                                                chartImageRef.current.delete(
                                                                    chartKey
                                                                );
                                                            }
                                                        }}
                                                        onLoad={() =>
                                                            handleChartLoad(chartKey)
                                                        }
                                                        onError={() =>
                                                            handleChartError(chartKey)
                                                        }
                                                        onClick={() =>
                                                            openGallery(
                                                                galleryIndex === -1
                                                                    ? 0
                                                                    : galleryIndex
                                                            )
                                                        }
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={(event) => {
                                                            if (event.key === 'Enter') {
                                                                openGallery(
                                                                    galleryIndex === -1
                                                                        ? 0
                                                                        : galleryIndex
                                                                );
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <span className="monthly-report__chart-empty">
                                                    -
                                                </span>
                                            )}
                                        </div>
                                        <div className="monthly-report__chart">-</div>
                                        <div className="monthly-report__chart">
                                            {canCreateChartFlow ? (
                                                <button
                                                    type="button"
                                                    className={`monthly-report__flow-btn${
                                                        flowLoading
                                                            ? ' monthly-report__flow-btn--loading'
                                                            : ''
                                                    }`}
                                                    onClick={() =>
                                                        handleCreateChartFlow(device)
                                                    }
                                                    disabled={flowDisabled}
                                                >
                                                    {flowLoading && (
                                                        <span
                                                            className="monthly-report__flow-spinner"
                                                            aria-hidden="true"
                                                        />
                                                    )}
                                                    {flowLabel}
                                                </button>
                                            ) : (
                                                <span className="monthly-report__chart-empty">
                                                    -
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                ))
            )}

            {isGalleryOpen && galleryItems.length > 0 && (
                <div
                    className="monthly-report__gallery-overlay"
                    onClick={() => setIsGalleryOpen(false)}
                >
                    <div
                        className="monthly-report__gallery"
                        onClick={(event) => event.stopPropagation()}
                        ref={galleryRef}
                    >
                        <div className="monthly-report__gallery-header">
                            <div className="monthly-report__gallery-title">
                                {galleryItems[galleryIndex]?.name || '圖表'}
                            </div>
                            <div className="monthly-report__gallery-actions">
                                <button
                                    type="button"
                                    className="monthly-report__gallery-btn"
                                    onClick={toggleFullscreen}
                                >
                                    全螢幕
                                </button>
                                <button
                                    type="button"
                                    className="monthly-report__gallery-btn"
                                    onClick={() => setIsGalleryOpen(false)}
                                >
                                    關閉
                                </button>
                            </div>
                        </div>
                        <div className="monthly-report__gallery-body">
                            <button
                                type="button"
                                className="monthly-report__gallery-nav monthly-report__gallery-nav--left"
                                onClick={goPrev}
                                aria-label="上一張"
                            >
                                ‹
                            </button>
                            <div className="monthly-report__gallery-image">
                                {galleryItems[galleryIndex]?.failed ? (
                                    <div className="monthly-report__gallery-placeholder">
                                        -
                                    </div>
                                ) : (
                                    <img
                                        src={galleryItems[galleryIndex]?.url}
                                        alt={galleryItems[galleryIndex]?.name || '圖表'}
                                    />
                                )}
                            </div>
                            <button
                                type="button"
                                className="monthly-report__gallery-nav monthly-report__gallery-nav--right"
                                onClick={goNext}
                                aria-label="下一張"
                            >
                                ›
                            </button>
                        </div>
                        <div
                            className="monthly-report__gallery-thumbs"
                            ref={thumbsRef}
                        >
                            {galleryItems.map((item, index) => (
                                <button
                                    key={`${item.id}-${item.url}`}
                                    type="button"
                                    className={`monthly-report__thumb${
                                        index === galleryIndex
                                            ? ' monthly-report__thumb--active'
                                            : ''
                                    }`}
                                    onClick={() => setGalleryIndex(index)}
                                >
                                    {item.failed ? (
                                        <span className="monthly-report__thumb-empty">
                                            -
                                        </span>
                                    ) : (
                                        <img
                                            src={item.url}
                                            alt={`${item.name || '圖表'}縮圖`}
                                        />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
